import React from "react";
import * as Yup from "yup";
import moment from "moment";
import { useParams } from "react-router-dom";
import { Row, Col, Spinner, Button, Card, Label } from "reactstrap";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from 'primereact/inputtextarea';
import EditorContainer from "../../components/EditorContainer";
import Address from "../../components/Address";
import { createOrder, updateOrder, getOrder, setOrderStatus } from "../../api/orders";
import { getTenant } from "../../api/tenants";
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import { it } from "../../utils/content";
import { canUserSave } from "../../auth/Can";
import roles from "../../auth/roles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// const orderStates = [{ label: "inserito", value: "inserito"}]

const schema = () =>
    Yup.object({
        /* order_ref: Yup.string()
            .required("Riferimento ordine obbligatorio")
            .min(2, `Riferimento ordine minimo 2 caratteri`), */
        created_for: Yup.string()
            .required("Il Cliente è obbligatorio"),
        model: Yup.string()
            .required("Il modello è obbligatorio")
            .nullable("Il modello è obbligatorio"),
        quantity: Yup.number()
            .required("Inserire la quantitá di dispositivi")
            .nullable().typeError('Inserire la quantitá di dispositivi'),
        /* delivery_date: Yup.string()
            .ensure("La data di richiesta consegna é obbligatoria")
            .required("La data di richiesta consegna é obbligatoria")
            .min(8, `La data di richiesta consegna é obbligatoria`) */
    });

const OrderEditor = ({ readOnly, activeTenant, isBackOffice }) => {
    const { id } = useParams();
    const { domains, user } = useAuth0();
    const [tenant, setTenant] = React.useState(activeTenant);

    const onCreate = async entity => {
        const result = await createOrder({
            ...entity,
            created_for: tenant.name,
            creator_email: user.email,
            tenant_id: tenant._id
        });
        return result;
    };

    const onLoad = async id => {
        const order = await getOrder(id);
        const tenantDett = await getTenant(order.tenant_id);
        setTenant(tenantDett);
        return order;
    };

    const onUpdate = async entity => {
        const result = await updateOrder(entity);
        return result;
    };

    const onAccept = async (setLoading, onRefresh) => {
        if (!confirm("Confermi di accettare l'ordine?")) return;
        try {
            setLoading(true)
            await setOrderStatus(id, "lavorazione");
            await onRefresh()
        } finally {
            setLoading(false);
        }
    }

    const onReject = async (setLoading, onRefresh) => {
        if (!confirm("Confermi di voler rifiutare l'ordine?")) return;
        try {
            setLoading(true)
            await setOrderStatus(id, "rifiutato");
            await onRefresh()
        } finally {
            setLoading(false);
        }
    }

    return (
        <EditorContainer
            key={id ? `${id}-order` : "new-order"}
            canSave={canUserSave([roles.DEALER, roles.ADMIN])}
            readOnly={readOnly ? true : false}
            schema={schema}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onLoad={onLoad}
            optionalNav={{ link: `/${isBackOffice ? 'backoffice' : 'tenant'}/orders`, text: "Ordini" }}
            redirRoot={`/${isBackOffice ? 'backoffice' : 'tenant'}/orders`}
            title={el =>
                el._id ? `Ordine Numero: ${el._id} in stato ${el.status.toUpperCase()}` : `NUOVO ORDINE`
            }
            children={(frmProps, containerHandleChange, setLoading, onRefresh) => {
                const inserted = frmProps.values["status"] === "inserito";
                const blockEdit = frmProps.values["status"] && !inserted;
                const processing = frmProps.values["status"] === "lavorazione";
                return (
                    <Row>
                        {user.isInRole([roles.ADMIN]) && inserted && (
                            <Col sm="12" xs="12" className="mt-2 mb-2">
                                <strong>Accetta / Rifiuta Ordine</strong>
                                <div>
                                    <Button color="success" className="mr-2" onClick={() => onAccept(setLoading, onRefresh)}><FontAwesomeIcon icon="check-circle" /> Accetta</Button>
                                    <Button color="danger" onClick={() => onReject(setLoading, onRefresh)}><FontAwesomeIcon icon="times-circle" /> Rifiuta </Button>
                                </div>
                            </Col>
                        )}
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Modello Dispositivo</strong>
                            <Dropdown
                                disabled={readOnly || blockEdit ? true : false}
                                filter
                                filterBy="label"
                                id="model"
                                className={frmProps.errors.model ? "p-error" : ""}
                                value={frmProps.values["model"]}
                                showClear={true}
                                style={{ width: "100%" }}
                                options={domains.devicesType}
                                onChange={e => containerHandleChange("model", e)}
                            />
                            {frmProps.errors.model ? (<div className="invalid-feedback">{frmProps.errors.model}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Quantitá</strong>
                            <InputText
                                disabled={readOnly || blockEdit ? true : false}
                                className={frmProps.errors.quantity ? "p-error" : ""}
                                value={frmProps.values["quantity"] || ""}
                                style={{ width: "100%" }}
                                keyfilter="pint"
                                onChange={e => containerHandleChange("quantity", e)}
                            />
                            {frmProps.errors.quantity ? (<div className="invalid-feedback">{frmProps.errors.quantity}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Cliente</strong>
                            <InputText
                                disabled={readOnly || blockEdit ? true : false}
                                className={frmProps.errors.created_for ? "p-error" : ""}
                                value={frmProps.values["created_for"] || ""}
                                style={{ width: "100%" }}
                                onChange={e => containerHandleChange("created_for", e)}
                            />
                            {frmProps.errors.created_for ? (<div className="invalid-feedback">{frmProps.errors.created_for}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Riferimento Ordine</strong>
                            <InputText
                                disabled={readOnly || blockEdit ? true : false}
                                className={frmProps.errors.order_ref ? "p-error" : ""}
                                value={frmProps.values["order_ref"] || ""}
                                style={{ width: "100%" }}
                                onChange={e => containerHandleChange("order_ref", e)}
                            />
                            {frmProps.errors.order_ref ? (<div className="invalid-feedback">{frmProps.errors.order_ref}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Note</strong>
                            <InputTextarea
                                className={frmProps.errors.notes ? "p-error" : ""}
                                rows={2}
                                value={frmProps.values["notes"] || ""}
                                style={{ width: "100%" }}
                                onChange={e => containerHandleChange("notes", e)}
                            />
                            {frmProps.errors.notes ? (<div className="invalid-feedback">{frmProps.errors.notes}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                        </Col>
                        <Col sm="6" xs="12">
                            <div className="mt-2"><strong>Data Consegna Richiesta</strong></div>
                            <Calendar
                                locale={it}
                                disabled={(readOnly || blockEdit) && !processing ? true : false}
                                monthNavigator={true}
                                yearNavigator={true}
                                showButtonBar={true}
                                className={frmProps.errors.delivery_date ? "p-error" : ""}
                                yearRange="1900:2099"
                                value={
                                    frmProps.values["delivery_date"]
                                        ? moment(frmProps.values["delivery_date"]).toDate()
                                        : ""
                                }
                                onChange={e => containerHandleChange("delivery_date", e)}
                                dateFormat="dd/mm/yy"
                                showIcon={true}
                            />
                            {frmProps.errors.delivery_date ? (<div className="invalid-feedback">{frmProps.errors.delivery_date}</div>) : null}
                        </Col>
                    </Row>
                );
            }}
            renderDealer={() =>
                tenant ? (
                    <React.Fragment>
                        <Card body className="rounded bg-secondary mb-2">
                            <Row>
                                <Col sm="4" xs="12">
                                    <strong>Dealer: </strong>
                                    <Label className="h5">{tenant.name}</Label>
                                </Col>
                                <Col sm="4" xs="12" md="3">
                                    <strong>Telefono: </strong>
                                    <Label className="h5">{tenant.phone}</Label>
                                </Col>
                                <Col sm="4" xs="12" md="3">
                                    <strong>Email: </strong>
                                    <Label className="h5">{tenant.email}</Label>
                                </Col>
                            </Row>
                        </Card>
                    </React.Fragment>
                ) : (
                        <Spinner style={{ width: "3rem", height: "3rem" }} type="grow" />
                    )
            }
        />
    );
};
export default OrderEditor;
